// src/handlers/locationHandlers.js
// ============================================================
// LOCATION & SUBLOCATION HANDLERS
// ============================================================
// Manages tap locations and their sublocations:
// - Creating/editing tap locations (e.g., "Bar", "Back Room")
// - Creating/editing sublocations (e.g., "Main Tap", "Guest Tap")
// - Handling location deletion with cascading keg reassignment
//
// Important: Deleting a location that has kegs assigned will
// unassign those kegs and reload them from database.
// ============================================================

import * as SupabaseService from '../supabaseService';
import * as UsageService from '../services/usageService';

export const createLocationHandlers = (state) => {
  const {
    locations, setLocations,
    kegs, setKegs,
    newLocation, setNewLocation,
    editingLocation, setEditingLocation,
    newSublocation, setNewSublocation,
    editingSublocation, setEditingSublocation,
    setShowLocationModal,
    showSublocationModal, setShowSublocationModal
  } = state;

  const handleAddOrUpdateLocation = async () => {
    if (editingLocation) {
      const result = await SupabaseService.updateLocation(editingLocation.id, {
        name: newLocation.name,
        description: newLocation.description,
        hasTaps: newLocation.hasTaps,
        tapCount: newLocation.tapCount,
        tapNames: newLocation.tapNames
      });
      if (result.success) {
        setLocations(prev => prev.map(loc =>
          loc.id === editingLocation.id
            ? { ...loc, name: newLocation.name, description: newLocation.description, hasTaps: newLocation.hasTaps, tapCount: newLocation.tapCount, tapNames: newLocation.tapNames }
            : loc
        ));
        setShowLocationModal(false);
        setEditingLocation(null);
        setNewLocation({ name: '', description: '', hasTaps: false, tapCount: 0, tapNames: [] });
      } else {
        alert('Error updating location: ' + result.error);
      }
    } else {
      const selectedBrewery = state.breweries?.find(b => b.id === state.selectedBreweryId);
      if (selectedBrewery) {
        const canAdd = UsageService.canAddLocation(selectedBrewery.id, selectedBrewery.plan, locations);
        if (!canAdd.allowed) {
          alert(`Location Limit Reached\n\n${canAdd.message}\n\nYou have ${canAdd.current}/${canAdd.limit} location${canAdd.limit !== 1 ? 's' : ''}.`);
          return;
        }
      }

      const result = await SupabaseService.addLocation({
        name: newLocation.name,
        description: newLocation.description,
        hasTaps: newLocation.hasTaps,
        tapCount: newLocation.tapCount,
        tapNames: newLocation.tapNames,
        brewery_id: state.selectedBreweryId
      });
      if (result.success) {
        setLocations(prev => [...prev, result.data]);
        setShowLocationModal(false);
        setNewLocation({ name: '', description: '', hasTaps: false, tapCount: 0, tapNames: [] });
      } else {
        alert('Error adding location: ' + result.error);
      }
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setNewLocation({
      name: location.name,
      description: location.description || '',
      hasTaps: location.hasTaps || false,
      tapCount: location.tapCount || 0,
      tapNames: location.tapNames || []
    });
    setShowLocationModal(true);
  };

  const handleDeleteLocation = async (locationId) => {
    const kegsAtLocation = kegs.filter(k => {
      const matchingLocation = locations.find(loc => loc.id === locationId);
      return matchingLocation && k.location === matchingLocation.name;
    });

    const message = kegsAtLocation.length > 0
      ? `This location has ${kegsAtLocation.length} keg(s) assigned to it. These kegs will be unassigned. Delete location?`
      : 'Are you sure you want to delete this location?';

    if (!window.confirm(message)) return;

    const result = await SupabaseService.deleteLocation(locationId);
    
    if (result.success) {
      setLocations(prev => prev.filter(loc => loc.id !== locationId));
      
      if (kegsAtLocation.length > 0) {
        for (const keg of kegsAtLocation) {
          await SupabaseService.updateKeg(keg.id, {
            location: null,
            sublocation: null
          });
        }
        const kegsResult = await SupabaseService.fetchKegs();
        if (kegsResult.success !== false) {
          setKegs(kegsResult);
        }
      }
    } else {
      alert('Error deleting location: ' + result.error);
    }
  };

  const handleAddOrUpdateSublocation = async (parentId) => {
    if (parentId && !showSublocationModal && !editingSublocation) {
      setNewSublocation({ name: '', description: '', parentId: parentId, hasTaps: true, tapCount: 0, tapNames: [] });
      setShowSublocationModal(true);
      return;
    }

    if (editingSublocation) {
      const result = await SupabaseService.updateSublocation(editingSublocation.id, {
        name: newSublocation.name,
        description: newSublocation.description,
        hasTaps: newSublocation.hasTaps,
        tapCount: newSublocation.tapCount,
        tapNames: newSublocation.tapNames
      });
      if (result.success) {
        setLocations(prev => prev.map(loc =>
          loc.id === newSublocation.parentId
            ? { 
                ...loc, 
                sublocations: loc.sublocations.map(sub =>
                  sub.id === editingSublocation.id
                    ? { ...sub, name: newSublocation.name, description: newSublocation.description, hasTaps: newSublocation.hasTaps, tapCount: newSublocation.tapCount, tapNames: newSublocation.tapNames }
                    : sub
                )
              }
            : loc
        ));
        setShowSublocationModal(false);
        setEditingSublocation(null);
        setNewSublocation({ name: '', description: '', parentId: null, hasTaps: true, tapCount: 0, tapNames: [] });
      } else {
        alert('Error updating sublocation: ' + result.error);
      }
    } else {
      const locationId = parentId || newSublocation.parentId;
      if (!locationId) {
        setShowSublocationModal(true);
        return;
      }

      const selectedBrewery = state.breweries?.find(b => b.id === state.selectedBreweryId);
      if (selectedBrewery) {
        const canAdd = UsageService.canAddSublocation(selectedBrewery.id, selectedBrewery.plan, locations);
        if (!canAdd.allowed) {
          alert(`Sublocation Limit Reached\n\n${canAdd.message}\n\nYou have ${canAdd.current}/${canAdd.limit} sublocation${canAdd.limit !== 1 ? 's' : ''}.`);
          return;
        }
      }

      const result = await SupabaseService.addSublocation(locationId, {
        name: newSublocation.name,
        description: newSublocation.description,
        hasTaps: newSublocation.hasTaps,
        tapCount: newSublocation.tapCount,
        tapNames: newSublocation.tapNames
      });

      if (result.success) {
        setLocations(prev => prev.map(loc =>
          loc.id === locationId
            ? { ...loc, sublocations: [...loc.sublocations, result.data] }
            : loc
        ));
        setShowSublocationModal(false);
        setNewSublocation({ name: '', description: '', parentId: null, hasTaps: true, tapCount: 0, tapNames: [] });
      } else {
        alert('Error adding sublocation: ' + result.error);
      }
    }
  };

  const handleEditSublocation = (locationId, sublocation) => {
    setEditingSublocation(sublocation);
    setNewSublocation({
      name: sublocation.name,
      description: sublocation.description || '',
      parentId: locationId,
      hasTaps: sublocation.hasTaps ?? false,
      tapCount: sublocation.tapCount ?? 0,
      tapNames: Array.isArray(sublocation.tapNames) ? sublocation.tapNames : []
    });
    setShowSublocationModal(true);
  };

  const handleDeleteSublocation = async (locationId, sublocationId) => {
    if (!window.confirm('Are you sure you want to delete this sublocation?')) return;

    const result = await SupabaseService.deleteSublocation(sublocationId);
    
    if (result.success) {
      setLocations(prev => prev.map(loc =>
        loc.id === locationId
          ? { ...loc, sublocations: loc.sublocations.filter(s => s.id !== sublocationId) }
          : loc
      ));
    } else {
      alert('Error deleting sublocation: ' + result.error);
    }
  };

  return {
    handleAddOrUpdateLocation,
    handleEditLocation,
    handleDeleteLocation,
    handleAddOrUpdateSublocation,
    handleEditSublocation,
    handleDeleteSublocation
  };
};
